import Link from "next/link";

import { forwardRef, useContext, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { twMerge } from "tailwind-merge";
import { UrlObject } from "url";

import Checkbox from "components/Checkbox";
import { SwipperContext } from "components/Swipper";
import useMediaQuery from "hooks/useMediaQuery";

type TabsProps = {
  children: React.ReactNode;
  className?: string;
};

// eslint-disable-next-line react/display-name
export const Tabs = forwardRef(
  (
    { children, className }: TabsProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const { setEnabledSwipe } = useContext(SwipperContext);

    return (
      <nav
        ref={ref}
        className={twMerge(
          "flex",
          "min-w-0",
          "pt-[30px] md:pt-0",
          "ml-[15px] md:ml-0",
          "overflow-x-auto md:overflow-x-hidden",
          className
        )}
        onScroll={() => {
          setEnabledSwipe?.(false);
        }}
      >
        {children}
      </nav>
    );
  }
);

type TabProps = {
  children: React.ReactNode;
  href?: string | UrlObject;
  active?: boolean;
  className?: string;
  activeClassName?: string;
  onClick?: (_event: React.MouseEvent<HTMLSpanElement>) => void;
  tooltip?: string | React.ReactNode;
  nonShallow?: boolean;
  noContainer?: boolean;
  showRadio?: boolean;
  showCheckbox?: boolean;
  type?: "tab" | "link";
};

// eslint-disable-next-line react/display-name
export const Tab = forwardRef(
  (
    {
      href,
      children,
      active,
      className,
      activeClassName,
      onClick,
      tooltip,
      nonShallow,
      noContainer,
      type,
      showCheckbox,
      showRadio,
    }: TabProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ) => {
    const { mobile } = useMediaQuery();

    const content = useMemo(() => {
      if (tooltip) {
        if (noContainer) {
          return (
            <OverlayTrigger overlay={<Tooltip>{tooltip}</Tooltip>}>
              <>{children}</>
            </OverlayTrigger>
          );
        }

        return (
          <OverlayTrigger overlay={<Tooltip>{tooltip}</Tooltip>}>
            <div>{children}</div>
          </OverlayTrigger>
        );
      }

      return children;
    }, [children, noContainer, tooltip]);

    if (type === "link") {
      if (href) {
        return (
          <Link
            href={href}
            shallow={!nonShallow}
            prefetch={false}
            onClick={onClick}
            scroll={!mobile}
            className={twMerge(
              "block",
              "cursor-pointer",
              "font-medium",
              active && "text-smart-teal",
              className,
              active && activeClassName
            )}
            ref={ref}
          >
            {!mobile ? content : children}
          </Link>
        );
      }

      return (
        <a
          ref={ref}
          onClick={onClick}
          className={twMerge(
            "block",
            "cursor-pointer",
            "font-medium",
            "flex items-center space-x-[5px]",
            active && "text-smart-teal",
            className,
            active && activeClassName
          )}
        >
          {showCheckbox && <Checkbox checked={active} />}
          {showRadio && <Checkbox type="radio" checked={active} />}
          <span>{content}</span>
        </a>
      );
    }

    const tabClassName = twMerge(
      "cursor-pointer",
      "overflow-visible md:overflow-hidden",
      "text-ellipsis",
      "whitespace-nowrap",
      "bg-[#ccc]",
      "rounded-full",
      "block",
      "text-[14px]",
      "leading-[1]",
      "mr-[8px]",
      "md:max-w-[150px]",
      "md:min-w-[80px]",
      "relative",
      "text-center",
      "px-[16px] pt-[8px] pb-[9px]",
      "hover:text-black hover:bg-gray-gray/40",
      "hover:text-active-black",
      active &&
        "bg-[#0276be] hover:bg-[#0276be] font-medium text-white hover:text-white",
      className,
      active && activeClassName
    );

    if (href) {
      return (
        <Link
          href={href}
          shallow={!nonShallow}
          prefetch={false}
          onClick={onClick}
          scroll={!mobile}
          className={tabClassName}
          ref={ref}
        >
          {!mobile ? content : children}
        </Link>
      );
    }

    return (
      <a ref={ref} onClick={onClick} className={tabClassName}>
        {content}
      </a>
    );
  }
);
